import {
  ExtendedAccountInfo,
  IAMUserState,
  IOption,
  ISelectableOption,
  PrimitiveValueType,
  RecordsSearchFilterResponse,
  RowDataReducerState,
  SetupListNavInfoByDepartment,
  StoreState,
} from "../../../types";
import {
  DEFAULT_OPTION,
  NO_RESULTS_DEFAULT_ISelectableOption,
} from "../constants";

export const getDefaultStore = (stores: Array<StoreState>): IOption => {
  if (!stores || stores.length === 0) return DEFAULT_OPTION;
  const store = stores.find((store: StoreState) => store.defaultStore);
  if (!store)
    return {
      label: `${stores[0].id} - ${stores[0].name}`,
      value: stores[0].id,
      disabled: false,
    };
  return {
    label: `${store.id} - ${store.name}`,
    value: store.id,
    disabled: false,
  };
};

export const getSelectedStoreOption = (
  inputValue: string,
  extendedAccountInfo: ExtendedAccountInfo[],
): IOption => {
  if (!inputValue) return { label: "", value: "" };
  const accountInfo = extendedAccountInfo?.find(
    (accountInfo) => accountInfo.storeId === inputValue,
  );

  return accountInfo
    ? {
        label: `${accountInfo.storeId} - ${accountInfo.storeName}`,
        value: `${accountInfo.storeId}`,
      }
    : { label: inputValue, value: inputValue };
};

export const getStoreOptionsFromDetailsAPIResponse = (
  extendedAccountInfo: ExtendedAccountInfo[],
): ISelectableOption[] => {
  const storeOptions: ISelectableOption[] = extendedAccountInfo?.map(
    (accountInfo) => ({
      label: `${accountInfo.storeId} - ${accountInfo.storeName}`,
      value: `${accountInfo.storeId}`,
    }),
  );

  return storeOptions?.length > 0
    ? storeOptions
    : [NO_RESULTS_DEFAULT_ISelectableOption()];
};

export const getCompanyIdByStoreId = (
  storeId: string,
  extendedAccountInfo: ExtendedAccountInfo[],
): string => {
  const accountInfo = extendedAccountInfo?.find(
    (accountInfo) => accountInfo.storeId === storeId,
  );
  return accountInfo ? (accountInfo.companyId as string) : "";
};

export const getStoreIdByCompnayId = (
  existingStoreValue: string | undefined,
  companyId: string,
  extendedAccountInfo: ExtendedAccountInfo[],
): string | null => {
  const accountInfo = extendedAccountInfo?.find(
    (accountInfo) => accountInfo.companyId === companyId,
  );

  return accountInfo
    ? (accountInfo.storeId as string)
    : (existingStoreValue ?? "");
};

export const getCompanyIdByStoreIdFromTypeAheadResponse = (
  storeId: string,
  response: RecordsSearchFilterResponse,
  responseFields?: string[],
): string => {
  if (!responseFields) return "";
  const selectedStore = response.data.find(
    (row) => row[responseFields[0]] === storeId,
  );
  return selectedStore ? (selectedStore[responseFields[1]] as string) : "";
};

export const getUpdatedStoreValueFromCompanyId = ({
  storeKey,
  inputValue,
  iamUserState,
  rowDataReducerState,
}: {
  storeKey: string;
  inputValue: PrimitiveValueType;
  iamUserState: IAMUserState;
  rowDataReducerState: RowDataReducerState;
}): string | null => {
  const existingStoreValue = rowDataReducerState.rowData[storeKey] as string;
  return getStoreIdByCompnayId(
    existingStoreValue,
    inputValue as string,
    iamUserState.extendedAccountInfo,
  );
};

export const getDepartmentIdByStore = ({
  store,
  department,
  extendedAccountInfo,
  setupListNavInfoByDepartment,
}: {
  store: string;
  department: string;
  extendedAccountInfo: ExtendedAccountInfo[];
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
}): string => {
  const shouldFetchDepartmentIdFromNavInfo =
    department.toLocaleLowerCase() === "parts" ||
    department.toLocaleLowerCase() === "service";

  if (shouldFetchDepartmentIdFromNavInfo) {
    const departmentIdInfo = setupListNavInfoByDepartment?.[
      department
    ].departmentIds.find((departmentId) => departmentId.storeId === store);

    return departmentIdInfo ? departmentIdInfo.departmentId : "";
  }

  const accountInfo = extendedAccountInfo?.find(
    (accountInfo) => accountInfo.storeId === store,
  );
  return accountInfo ? accountInfo.departmentIds[0] : "";
};
